import { GetServerSidePropsContext, GetStaticPropsContext } from 'next';
import {
  PreprToolbarProps,
  PreprSegment,
  PreprHeaderName,
  PreprErrorCode,
} from '../../types';
import pjson from '../../../package.json';

/**
 * Custom error class for Prepr-related errors
 */
export class PreprError extends Error {
  constructor(
    message: string,
    public readonly code: PreprErrorCode,
    public readonly context?: string,
    public readonly originalError?: Error
  ) {
    super(message);
    this.name = 'PreprError';
  }
}

/**
 * Internal helper to get a specific Prepr header value from context
 * @param context - The Next.js context object from getServerSideProps or getStaticProps
 * @param name - The header name to retrieve
 * @returns The header value or null if not found
 */
function getPreprHeaderFromContext(
  context: GetServerSidePropsContext | GetStaticPropsContext,
  name: PreprHeaderName
): string | null {
  if ('req' in context && context.req) {
    const headers = context.req.headers;
    const headerValue = headers[name.toLowerCase()];
    return typeof headerValue === 'string'
      ? headerValue
      : headerValue?.[0] || null;
  }
  return null;
}

/**
 * Returns the Prepr Customer ID from the context headers
 * @param context - The Next.js context object
 * @returns Prepr Customer ID
 */
export function getPreprUUID(
  context: GetServerSidePropsContext | GetStaticPropsContext
): string | null {
  return getPreprHeaderFromContext(context, 'prepr-customer-id');
}

/**
 * Returns the active segment from the context headers
 * @param context - The Next.js context object
 * @returns Active segment
 */
export function getActiveSegment(
  context: GetServerSidePropsContext | GetStaticPropsContext
): string | null {
  return getPreprHeaderFromContext(context, 'Prepr-Segments');
}

/**
 * Returns the active variant from the context headers
 * @param context - The Next.js context object
 * @returns Active variant
 */
export function getActiveVariant(
  context: GetServerSidePropsContext | GetStaticPropsContext
): string | null {
  return getPreprHeaderFromContext(context, 'Prepr-ABtesting');
}

/**
 * Helper function to retrieve all Prepr headers from context
 * @param context - The Next.js context object
 * @returns Object with Prepr headers
 */
export function getPreprHeaders(
  context: GetServerSidePropsContext | GetStaticPropsContext
): Record<string, string> {
  const preprHeaders: Record<string, string> = {};

  if ('req' in context && context.req) {
    const headers = context.req.headers;
    Object.entries(headers).forEach(([key, value]) => {
      if (key.toLowerCase().startsWith('prepr')) {
        preprHeaders[key] =
          typeof value === 'string' ? value : value?.[0] || '';
      }
    });
  }

  return preprHeaders;
}

/**
 * Validates a Prepr GraphQL token
 * @param token - The token to validate
 * @returns Validation result with error details if invalid
 */
export function validatePreprToken(token: string): {
  valid: boolean;
  error?: string;
} {
  if (!token) {
    return { valid: false, error: 'Token is required' };
  }
  if (!token.startsWith('https://')) {
    return { valid: false, error: 'Token must be a valid HTTPS URL' };
  }
  return { valid: true };
}

/**
 * Checks if the current environment is in preview mode
 * @returns True if in preview mode
 */
export function isPreviewMode(): boolean {
  return process.env.PREPR_ENV === 'preview';
}

/**
 * Extracts the access token from a Prepr GraphQL URL
 * @param graphqlUrl - The full Prepr GraphQL URL
 * @returns The access token or null if invalid
 * @example
 * ```typescript
 * const token = extractAccessToken('https://graphql.prepr.io/abc123')
 * // Returns: 'abc123'
 * ```
 */
export function extractAccessToken(graphqlUrl: string): string | null {
  if (!graphqlUrl) return null;

  try {
    const url = new URL(graphqlUrl);
    if (url.hostname !== 'graphql.prepr.io') return null;

    const pathParts = url.pathname.split('/');
    const token = pathParts[pathParts.length - 1];

    return token && token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

/**
 * Fetches the segments from the Prepr API
 * @param token Prepr GraphQL URL with scope 'segments'
 * @returns Array of PreprSegment
 * @throws PreprError if the request fails
 */
export async function getPreprEnvironmentSegments(
  token: string
): Promise<PreprSegment[]> {
  const validation = validatePreprToken(token);
  if (!validation.valid) {
    throw new PreprError(
      validation.error!,
      'INVALID_TOKEN',
      'getPreprEnvironmentSegments'
    );
  }

  try {
    const response = await fetch(token, {
      headers: {
        'User-Agent': `Prepr-Preview-Bar/${getPackageVersion()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        query: `{
                _Segments {
                    _id
                    name
                }
            }`,
      }),
    });

    if (!response.ok) {
      throw new PreprError(
        `HTTP ${response.status}: ${response.statusText}`,
        'HTTP_ERROR',
        'getPreprEnvironmentSegments'
      );
    }

    const json = await response.json();

    if (!json || !json.data || !json.data._Segments) {
      throw new PreprError(
        'Invalid response format from Prepr API',
        'INVALID_RESPONSE',
        'getPreprEnvironmentSegments'
      );
    }

    return json.data._Segments as PreprSegment[];
  } catch (error) {
    if (error instanceof PreprError) {
      throw error;
    }
    throw new PreprError(
      'Failed to fetch segments from Prepr API',
      'FETCH_ERROR',
      'getPreprEnvironmentSegments',
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

/**
 * Fetches all the necessary toolbar props for Pages directory
 * @param token Prepr GraphQL URL with scope 'segments'
 * @param context The Next.js context object from getServerSideProps
 * @returns Object with activeSegment, activeVariant and data
 */
export async function getToolbarProps(
  token: string,
  context: GetServerSidePropsContext | GetStaticPropsContext
): Promise<PreprToolbarProps> {
  let data: PreprSegment[] = [];
  let activeSegment: string | null = null;
  let activeVariant: string | null = null;

  // Prevent unnecessary function calling in production
  if (isPreviewMode()) {
    try {
      data = await getPreprEnvironmentSegments(token);
      activeSegment = getActiveSegment(context);
      activeVariant = getActiveVariant(context);
    } catch (error) {
      // In preview mode, we should still return props even if API fails
      console.error('Failed to fetch toolbar props:', error);
      // Return empty data to prevent toolbar from crashing
      data = [];
    }
  }

  return {
    activeSegment,
    activeVariant,
    data,
  };
}

/**
 * Alias for getToolbarProps for consistency with App Router API
 */
export const getPreviewBarProps = getToolbarProps;

function getPackageVersion() {
  return pjson.version;
}

// Re-export the middleware for convenience
export { default as createPreprMiddleware } from '../../middleware';
